import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useSearchParams, Link } from "react-router-dom";

const inputCls = "w-full border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none transition-colors focus:border-accent";

const EMPTY = {
  project_id: "", site_id: "", title: "", description: "", date: "",
  start_time: "08:00", end_time: "17:00", all_day: false, break_minutes: 60,
  type: "work", staff_ids: [], manager_id: "", required_count: 0, status: "予定", notes: "",
};

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

const TYPE_COLORS = { work: "border-accent/50 text-accent", off: "border-line text-gray-400", leave: "border-amber-500/50 text-amber-400" };
const TYPE_LABELS = { work: "作業", off: "休日", leave: "休暇" };

export default function AdminSchedules() {
  const [params] = useSearchParams();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [sites, setSites] = useState([]);
  const [staff, setStaff] = useState([]);
  const [filterProject, setFilterProject] = useState(params.get("project_id") || "");
  const [filterStaff, setFilterStaff] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [multiDates, setMultiDates] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");

  const range = useMemo(() => {
    const last = new Date(year, month, 0).getDate();
    const mm = String(month).padStart(2, "0");
    return { from: `${year}-${mm}-01`, to: `${year}-${mm}-${String(last).padStart(2, "0")}` };
  }, [year, month]);

  const load = () => {
    const params = { date_from: range.from, date_to: range.to };
    if (filterProject) params.project_id = filterProject;
    if (filterStaff) params.staff_id = filterStaff;
    api.get("/admin/schedules", { params }).then((r) => setItems(r.data)).catch(() => toast.error("取得に失敗しました"));
  };

  useEffect(() => {
    api.get("/admin/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/admin/sites").then((r) => setSites(r.data)).catch(() => {});
    api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {});
    api.get("/admin/schedule-templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);

  const applyTemplate = (id) => {
    setTemplateId(id);
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setForm((f) => ({ ...f, title: t.title || f.title, start_time: t.start_time || f.start_time, end_time: t.end_time || f.end_time, break_minutes: t.break_minutes, type: t.type || f.type, description: t.description || f.description }));
  };

  const saveTemplate = async () => {
    const name = window.prompt("テンプレート名（例：通常作業 8:00-17:00）", form.title || "");
    if (!name) return;
    try {
      const r = await api.post("/admin/schedule-templates", {
        name, title: form.title, start_time: form.start_time, end_time: form.end_time,
        break_minutes: Number(form.break_minutes) || 0, type: form.type, description: form.description,
      });
      setTemplates((arr) => [...arr, r.data]);
      toast.success("テンプレートを保存しました");
    } catch {
      toast.error("テンプレートの保存に失敗しました");
    }
  };

  const removeTemplate = async () => {
    if (!templateId || !window.confirm("このテンプレートを削除しますか？")) return;
    try {
      await api.delete(`/admin/schedule-templates/${templateId}`);
      setTemplates((arr) => arr.filter((t) => t.id !== templateId));
      setTemplateId("");
      toast.success("削除しました");
    } catch {
      toast.error("削除に失敗しました");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, filterProject, filterStaff]);

  const move = (delta) => {
    let m = month + delta, y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setYear(y); setMonth(m);
  };

  const byDate = useMemo(() => {
    const map = {};
    items.forEach((s) => { (map[s.date] = map[s.date] || []).push(s); });
    return map;
  }, [items]);

  const calendarCells = useMemo(() => {
    const first = new Date(year, month - 1, 1);
    const last = new Date(year, month, 0).getDate();
    const cells = [];
    for (let i = 0; i < first.getDay(); i++) cells.push(null);
    for (let d = 1; d <= last; d++) {
      cells.push(`${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
    }
    return cells;
  }, [year, month]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const toggleStaff = (id) =>
    setForm((f) => ({ ...f, staff_ids: f.staff_ids.includes(id) ? f.staff_ids.filter((x) => x !== id) : [...f.staff_ids, id] }));

  const openNew = (date) => {
    setForm({ ...EMPTY, date: date || range.from, project_id: filterProject });
    setMultiDates([]);
    setWarnings([]);
    setEditing({});
  };

  const openEdit = (s) => {
    setForm({ ...EMPTY, ...s });
    setMultiDates([]);
    setWarnings([]);
    setEditing(s);
  };

  const addDate = (d) => {
    if (!d || multiDates.includes(d) || d === form.date) return;
    setMultiDates((arr) => [...arr, d].sort());
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setWarnings([]);
    try {
      let res;
      if (editing.id) {
        res = await api.put(`/admin/schedules/${editing.id}`, form);
      } else if (multiDates.length > 0) {
        res = await api.post("/admin/schedules/bulk", { base: form, dates: [form.date, ...multiDates] });
        toast.success(`${res.data.created}件の予定を登録しました`);
      } else {
        res = await api.post("/admin/schedules", form);
      }
      const ws = res.data.warnings || [];
      if (ws.length > 0) {
        setWarnings(ws);
        toast.warning("重複の注意があります（登録は完了）");
      } else {
        toast.success("保存しました");
        setEditing(null);
      }
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`「${s.date} ${s.title}」を削除しますか？スタッフに中止の通知が送られます。`)) return;
    try {
      await api.delete(`/admin/schedules/${s.id}`);
      toast.success("削除しました");
      load();
    } catch {
      toast.error("削除に失敗しました");
    }
  };

  const formSites = sites.filter((s) => !form.project_id || s.project_id === form.project_id);
  const projectName = (id) => projects.find((p) => p.id === id)?.name || "";

  return (
    <div data-testid="admin-schedules-page">
      {params.get("project_id") && <Link data-testid="schedules-back-workflow" to={`/admin/workflow/${params.get("project_id")}?tab=site`} className="mb-4 inline-block text-sm text-accent">← 案件の現場管理へ戻る</Link>}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-mincho text-2xl font-bold text-white">作業予定カレンダー</h1>
          <p className="mt-1 font-mono text-[10px] tracking-[0.3em] text-gray-500">SCHEDULES — 予定の登録・変更でスタッフに通知されます</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select data-testid="schedules-filter-project" value={filterProject} onChange={(e) => setFilterProject(e.target.value)} className="border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none focus:border-accent">
            <option value="">すべての案件</option>
            {projects.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
          </select>
          <select data-testid="schedules-filter-staff" value={filterStaff} onChange={(e) => setFilterStaff(e.target.value)} className="border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none focus:border-accent">
            <option value="">すべてのスタッフ</option>
            {staff.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
          </select>
          <button data-testid="schedule-add-button" onClick={() => openNew()} className="flex items-center gap-2 bg-accent px-5 py-2.5 font-display text-xs font-bold tracking-[0.15em] text-ink transition-colors hover:bg-accent-hover">
            <Plus className="h-4 w-4" />
            予定を登録
          </button>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between">
        <button data-testid="schedules-prev" onClick={() => move(-1)} className="border border-line p-2 text-gray-400 transition-colors hover:border-accent hover:text-accent"><ChevronLeft className="h-4 w-4" /></button>
        <h2 data-testid="schedules-month" className="font-mincho text-lg font-bold text-white">{year}年{month}月</h2>
        <button data-testid="schedules-next" onClick={() => move(1)} className="border border-line p-2 text-gray-400 transition-colors hover:border-accent hover:text-accent"><ChevronRight className="h-4 w-4" /></button>
      </div>

      <div className="mt-4 grid grid-cols-7 border border-line">
        {["日", "月", "火", "水", "木", "金", "土"].map((d, i) => (
          <div key={d} className={`border-b border-line bg-coal px-1 py-2 text-center font-mono text-[10px] tracking-[0.2em] ${i === 0 ? "text-red-400" : i === 6 ? "text-sky-400" : "text-gray-500"}`}>{d}</div>
        ))}
        {calendarCells.map((date, i) => (
          <div
            key={i}
            data-testid={date ? `schedule-day-${date}` : undefined}
            onClick={() => date && openNew(date)}
            className={`min-h-[90px] cursor-pointer border-b border-r border-line p-1 transition-colors last:border-r-0 hover:bg-coal/60 ${date ? "bg-panel" : "bg-ink/50"}`}
          >
            {date && (
              <>
                <p className="px-1 text-right font-mono text-[10px] text-gray-500">{Number(date.slice(-2))}</p>
                <div className="mt-0.5 space-y-0.5">
                  {(byDate[date] || []).slice(0, 3).map((s) => (
                    <button
                      key={s.id}
                      data-testid={`schedule-chip-${s.id}`}
                      onClick={(e) => { e.stopPropagation(); openEdit(s); }}
                      className={`block w-full truncate border-l-2 px-1 text-left text-[10px] ${TYPE_COLORS[s.type] || TYPE_COLORS.work} ${s.status === "中止" ? "opacity-40 line-through" : "hover:bg-accent/10"}`}
                    >
                      {s.start_time} {s.title}
                    </button>
                  ))}
                  {(byDate[date] || []).length > 3 && <p className="px-1 text-[9px] text-gray-600">+{byDate[date].length - 3}</p>}
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm">
          <form data-testid="schedule-form" onSubmit={save} className="max-h-[90vh] w-full max-w-2xl space-y-4 overflow-y-auto border border-line bg-coal p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-mincho text-lg font-bold text-white">{editing.id ? "予定を編集" : "予定を登録"}</h2>
              <button type="button" data-testid="schedule-form-close" onClick={() => setEditing(null)} className="text-gray-500 hover:text-white"><X className="h-5 w-5" /></button>
            </div>

            {warnings.length > 0 && (
              <div data-testid="schedule-warnings" className="border border-amber-500/40 bg-amber-500/10 p-3">
                {warnings.map((w, i) => (
                  <p key={i} className="flex items-center gap-2 text-xs text-amber-400"><AlertTriangle className="h-3.5 w-3.5 shrink-0" />{w}</p>
                ))}
              </div>
            )}

            {templates.length > 0 || !editing.id ? (
              <div className="flex items-center gap-2 border border-line bg-ink p-2">
                <select data-testid="schedule-template-select" value={templateId} onChange={(e) => applyTemplate(e.target.value)} className="flex-1 border border-line bg-coal px-2 py-2 text-xs text-gray-300 outline-none focus:border-accent">
                  <option value="">テンプレートから入力…</option>
                  {templates.map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
                </select>
                <button type="button" data-testid="schedule-template-save" onClick={saveTemplate} className="shrink-0 border border-line px-3 py-2 text-[11px] text-gray-400 transition-colors hover:border-accent hover:text-accent">
                  定型保存
                </button>
                {templateId && (
                  <button type="button" data-testid="schedule-template-delete" onClick={removeTemplate} className="shrink-0 border border-line px-3 py-2 text-[11px] text-gray-400 transition-colors hover:border-red-500 hover:text-red-400">
                    削除
                  </button>
                )}
              </div>
            ) : null}

            <Field label="タイトル *"><input data-testid="schedule-form-title" required value={form.title} onChange={set("title")} className={inputCls} placeholder="基礎工事 1日目" /></Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="日付 *"><input data-testid="schedule-form-date" type="date" required value={form.date} onChange={set("date")} className={inputCls} /></Field>
              <Field label="種類">
                <select data-testid="schedule-form-type" value={form.type} onChange={set("type")} className={inputCls}>
                  <option value="work">作業</option><option value="off">休日</option><option value="leave">休暇</option>
                </select>
              </Field>
              <Field label="開始時刻"><input data-testid="schedule-form-start" type="time" value={form.start_time} onChange={set("start_time")} className={inputCls} /></Field>
              <Field label="終了時刻"><input data-testid="schedule-form-end" type="time" value={form.end_time} onChange={set("end_time")} className={inputCls} /></Field>
              <Field label="休憩（分）"><input data-testid="schedule-form-break" type="number" min="0" value={form.break_minutes} onChange={set("break_minutes")} className={inputCls} /></Field>
              <Field label="必要人数"><input data-testid="schedule-form-required" type="number" min="0" value={form.required_count} onChange={set("required_count")} className={inputCls} /></Field>
              <Field label="工事案件">
                <select data-testid="schedule-form-project" value={form.project_id} onChange={(e) => setForm((f) => ({ ...f, project_id: e.target.value, site_id: "" }))} className={inputCls}>
                  <option value="">—</option>
                  {projects.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
                </select>
              </Field>
              <Field label="現場">
                <select data-testid="schedule-form-site" value={form.site_id} onChange={set("site_id")} className={inputCls}>
                  <option value="">—</option>
                  {formSites.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                </select>
              </Field>
              <Field label="責任者">
                <select data-testid="schedule-form-manager" value={form.manager_id} onChange={set("manager_id")} className={inputCls}>
                  <option value="">—</option>
                  {staff.filter((s) => s.is_active).map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                </select>
              </Field>
              <Field label="状態">
                <select data-testid="schedule-form-status" value={form.status} onChange={set("status")} className={inputCls}>
                  <option>予定</option><option>確定</option><option>延期</option><option>中止</option>
                </select>
              </Field>
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-300">
              <input data-testid="schedule-form-allday" type="checkbox" checked={form.all_day} onChange={set("all_day")} className="h-4 w-4 accent-[#ff8c1a]" />
              終日
            </label>

            {!editing.id && (
              <Field label="追加日（連日・飛び日・繰り返しを日付ごとに登録）">
                <div className="flex gap-2">
                  <input data-testid="schedule-form-add-date" type="date" id="schedule-extra-date" className={inputCls} />
                  <button type="button" data-testid="schedule-form-add-date-button" onClick={() => { const el = document.getElementById("schedule-extra-date"); addDate(el.value); el.value = ""; }} className="shrink-0 border border-line px-4 text-xs text-gray-300 transition-colors hover:border-accent hover:text-accent">
                    追加
                  </button>
                </div>
                {multiDates.length > 0 && (
                  <div data-testid="schedule-form-dates" className="mt-2 flex flex-wrap gap-1.5">
                    {multiDates.map((d) => (
                      <span key={d} className="flex items-center gap-1 border border-accent/40 bg-accent/10 px-2 py-1 font-mono text-[10px] text-accent">
                        {d}
                        <button type="button" data-testid={`schedule-date-remove-${d}`} onClick={() => setMultiDates((arr) => arr.filter((x) => x !== d))}><X className="h-3 w-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </Field>
            )}

            <Field label="担当スタッフ（複数選択可）">
              <div data-testid="schedule-form-staff" className="flex flex-wrap gap-1.5 border border-line bg-ink p-2">
                {staff.filter((s) => s.is_active).map((s) => (
                  <button key={s.id} type="button" data-testid={`schedule-staff-${s.id}`} onClick={() => toggleStaff(s.id)} className={`border px-2.5 py-1.5 text-xs transition-colors ${form.staff_ids.includes(s.id) ? "border-accent bg-accent/10 text-accent" : "border-line text-gray-400 hover:text-white"}`}>
                    {s.name}
                  </button>
                ))}
                {staff.filter((s) => s.is_active).length === 0 && <p className="text-xs text-gray-600">有効なスタッフがいません</p>}
              </div>
            </Field>

            <Field label="作業内容"><textarea data-testid="schedule-form-description" rows={2} value={form.description} onChange={set("description")} className={`resize-y ${inputCls}`} /></Field>
            <Field label="備考"><textarea data-testid="schedule-form-notes" rows={2} value={form.notes} onChange={set("notes")} className={`resize-y ${inputCls}`} /></Field>

            <div className="flex gap-2">
              <button data-testid="schedule-form-submit" type="submit" disabled={saving} className="flex-1 bg-accent py-3 font-display text-sm font-bold tracking-[0.2em] text-ink transition-colors hover:bg-accent-hover disabled:opacity-50">
                {saving ? "保存中..." : multiDates.length > 0 ? `${multiDates.length + 1}件まとめて登録` : "保存する"}
              </button>
              {editing.id && (
                <button type="button" data-testid="schedule-form-delete" onClick={() => { remove(editing); setEditing(null); }} className="border border-red-500/50 px-5 text-red-400 transition-colors hover:bg-red-500/10">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
